import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import {
  Box,
  Button,
  Text,
  useMedia,
  Layout,
  Popup,
} from '../../components/Boneyard';

import { hook, withValue } from '@g4/automation-hooks';
import { useSSRQuery } from '../../graphql';
import tracking from '@g4/g4-analytics';

import { greyA100 } from '../../theme/theme/colors';
import { UPLIFT_MERCH_BANNER } from '../../../automationHooks.json';

import { useTranslations } from '../../graphql/components/ApplicationQueryProvider';
import UPLIFT_MERCHANDISE_BANNER_QUERY from './upliftMerchandiseBanner/upliftMerchandiseBanner.query.graphql.js';

const Icon = styled.img`
  width: 80px;
  height: auto;
  margin-right: ${props => props.theme.spacing(3)};
  margin-left: ${props => props.theme.spacing(2)};
  flex-shrink: 0;

  ${props => props.theme.query.md} {
    margin-right: ${props => props.theme.spacing(4)};
  }

  ${props => props.theme.query.lg} {
    margin-right: ${props => props.theme.spacing(6)};
    margin-left: ${props => props.theme.spacing(8)};
  }
`;

const UpliftMerchandiseBanner = () => {
  const mq = useMedia();
  const isMobile = mq.lte('sm');
  const translations = useTranslations();

  const [isPopupVisible, setIsPopupVisible] = useState(false);

  const { description, ctaLabel } = translations.upliftMerchandiseBanner;

  const { loading, error, data } = useSSRQuery(UPLIFT_MERCHANDISE_BANNER_QUERY);

  const banner = data?.application?.upliftMerchandiseBanner;

  const promoData = useMemo(
    () =>
      banner && {
        name: 'Uplift Merchandise Banner',
        creative: banner.image
          ? banner.image.split('/').slice(-1)[0]
          : 'no icon',
        position: 'Home Page',
      },
    [banner]
  );

  useEffect(() => {
    if (!loading && promoData) {
      tracking.trackPromoImpressionExternal(promoData);
    }
  }, [loading, promoData]);

  if (loading) {
    return null;
  }

  if (error) {
    throw error;
  }

  const upliftSettings = data?.settings?.uplift;

  const shouldUpliftBannerDisplay = () => {
    if (!upliftSettings) {
      return false;
    }

    const isGlobalActive = upliftSettings.pages?.find(
      item => item.pageName === 'global' && item.pageValue === true
    );
    if (!isGlobalActive) {
      return false;
    }
    const isHomePageActive = upliftSettings.pages.find(
      item => item.pageName === 'home' && item.pageValue === true
    );
    if (!isHomePageActive) {
      return false;
    }
    return true;
  };

  const isUpliftBannerVisible = shouldUpliftBannerDisplay();

  if (!isUpliftBannerVisible) {
    return null;
  }

  const handleClick = () => {
    tracking.trackPromoClickExternal(promoData);
    setIsPopupVisible(true);
  };

  return (
    <React.Fragment>
      <Layout.FullWidth>
        <Layout.Strip color={greyA100}>
          <Box
            d="flex"
            direction={isMobile ? 'column' : 'row'}
            automationHook={UPLIFT_MERCH_BANNER.hook}
            pt={isMobile ? 6 : 3}
            pb={isMobile ? 4 : 3}
            alignItems="center"
            justifyContent={isMobile ? 'center' : 'space-between'}
            w={'100%'}
          >
            <Box
              d="flex"
              alignItems="center"
              mr={isMobile ? 0 : 4}
              mb={isMobile ? 4 : 0}
              pl={isMobile ? 1 : 0}
              pr={isMobile ? 1 : 0}
            >
              {banner.image && (
                <Icon
                  src={banner.image}
                  {...hook(UPLIFT_MERCH_BANNER.hook, 'icon')}
                />
              )}
              <Text
                size={mq.lt('md') ? 14 : mq.lte('md') ? 18 : 23}
                lh={1.2}
                weight={500}
                automationHook={withValue(
                  UPLIFT_MERCH_BANNER.hook,
                  'description'
                )}
              >
                {description}
              </Text>
            </Box>
            <Box w="200px">
              <Button
                automationHook={withValue(UPLIFT_MERCH_BANNER.hook, 'button')}
                type="secondary"
                onClick={handleClick}
                fullWidth
              >
                {ctaLabel}
              </Button>
            </Box>
          </Box>
        </Layout.Strip>
      </Layout.FullWidth>
      <Popup
        open={isPopupVisible}
        withDefaultPadding={false}
        onRequestClose={() => setIsPopupVisible(false)}
        automationHook={withValue(UPLIFT_MERCH_BANNER.hook, 'popup')}
      >
        <Box borderRadius={10} overflow="hidden">
          <img
            src={
              isMobile ? banner.mobilePopupImage.image : banner.popupImage.image
            }
            alt={
              isMobile
                ? banner.mobilePopupImage.alternateText
                : banner.popupImage.alternateText
            }
          />
        </Box>
      </Popup>
    </React.Fragment>
  );
};

export default UpliftMerchandiseBanner;
